import { useResizeObserver } from "client/Hooks";
import {
	PropsWithChildren,
	ReactElement,
	RefObject,
	useCallback,
	useLayoutEffect,
	useRef
} from "react";
import { ContainsDialogsWrapper } from "../ContainsDialogsWrapper/ContainsDialogsWrapper";
import classNames from "classnames";
import { VirtualListVerticalSizeAndPositionInformation } from "./VirtualList";
import styles from "./VirtualList.module.less";

export type VirtualListItemProps = {
	isPrerender: boolean;
	itemKey: string;
	topAndHeight: VirtualListVerticalSizeAndPositionInformation;
	shouldMeasureHeight: boolean;
	onHeightChanged: (itemKey: string, height: number) => void;
	onIsAlwaysVisibleChange: (itemKey: string, isAlwaysVisible: boolean) => void;
};

export const Item = (props: PropsWithChildren<VirtualListItemProps>): ReactElement => {
	const {
		isPrerender,
		itemKey,
		onHeightChanged,
		onIsAlwaysVisibleChange,
		shouldMeasureHeight,
		topAndHeight,
		children
	} = props;

	const doesContainDialogsChangeHandler = useCallback(
		(value: boolean): void => {
			onIsAlwaysVisibleChange(itemKey, value);
		},
		[itemKey, onIsAlwaysVisibleChange]
	);

	const renderContent = (): ReactElement => {
		if (!shouldMeasureHeight) {
			return (
				<InternalStaticItem
					topAndHeight={topAndHeight}
					isPrerender={isPrerender}
				>
					{children}
				</InternalStaticItem>
			);
		}

		return (
			<InternalDynamicItem
				itemKey={itemKey}
				topAndHeight={topAndHeight}
				isPrerender={isPrerender}
				onHeightChanged={onHeightChanged}
			>
				{children}
			</InternalDynamicItem>
		);
	};

	return (
		<ContainsDialogsWrapper onDoesContainDialogsChange={doesContainDialogsChangeHandler}>
			{renderContent()}
		</ContainsDialogsWrapper>
	);
};

Item.displayName = "VirtualList.Item";

type InternalStaticItemProps = {
	topAndHeight: VirtualListVerticalSizeAndPositionInformation;
	isPrerender: boolean | undefined;
};

const InternalStaticItem = (props: PropsWithChildren<InternalStaticItemProps>): ReactElement => {
	const {
		topAndHeight,
		isPrerender,
		children
	} = props;

	return (
		<div
			style={topAndHeight}
			className={classNames(
				styles.itemWrapper,
				{ [styles.itemWrapper_prerender]: isPrerender }
			)}
		>
			{children}
		</div>
	);
};

InternalStaticItem.displayName = "InternalStaticItem";

type InternalDynamicItemProps = {
	topAndHeight: VirtualListVerticalSizeAndPositionInformation;
	isPrerender: boolean | undefined;
	itemKey: string;
	onHeightChanged: (itemKey: string, height: number) => void;
};

const InternalDynamicItem = (props: PropsWithChildren<InternalDynamicItemProps>): ReactElement => {
	const {
		topAndHeight,
		isPrerender,
		itemKey,
		onHeightChanged,
		children
	} = props;

	const measurementContainerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const {
		height: measuredHeight,
		wasInitialized
	} = useResizeObserver(measurementContainerRef);

	useLayoutEffect(
		() => {
			if (wasInitialized) {
				onHeightChanged(itemKey, measuredHeight);
			}
		},
		[
			itemKey,
			measuredHeight,
			onHeightChanged,
			wasInitialized
		]
	);

	return (
		<InternalStaticItem
			isPrerender={isPrerender}
			topAndHeight={topAndHeight}
		>
			<div
				ref={measurementContainerRef}
			>
				{children}
			</div>
		</InternalStaticItem>
	);
};

InternalDynamicItem.displayName = "InternalDynamicItem";
